import {useContainer} from "#imports";
import {DependencyInjectId} from "@@/package/const/DependencyInjectId";
import {applicationContext} from "@@/package/application/applicationContext";
import {useUserStore} from "@/store/user/userStore";
import {UserModel} from "@@/package/mapper/user/model/UserModel";
import UserMapper from "@@/package/mapper/user/UserMapper";
import SubscriptionMapper from "~~/package/mapper/subscription/SubscriptionMapper";
import type UserAccountRequest from "~~/package/apis/user/request/UserAccountRequest";
import {CountryModel} from "@@/package/mapper/user/model/CountryModel";
import {UserV2Model} from "~~/package/mapper/user/model/UserV2Model";
import {useNuxtApp} from "#app";
import { UserDashboardSummaryModel } from "~~/package/mapper/user/model/UserDashboardSummaryModel";
import {UserPasswordChangeModel} from '~~/package/mapper/user/model/UserPasswordChangeModel';
import type {ResendEmailType} from '~~/package/mapper/user/model/ResendEmailType';

export const useUserService = () => {
    const config = { public: applicationContext.getConfig() }
    const userMapper = useContainer<UserMapper>(DependencyInjectId.UserMapper);
    const { $i18n } = useNuxtApp();

    const setXCountry = (): Promise<void> => {
        return userMapper.setXCountry();
    };

    const setUser = async (isSetI18n = true): Promise<void> => {
        const userStore = useUserStore();
        const user: UserV2Model = await userMapper.getUserV2(new Date().getTime());
        if(isSetI18n) {
            $i18n.setLocale(user.locale.toLocaleLowerCase());
        }
        userStore.updateUserV2(user);

        if (user.orgInfo != null) {
            const subscriptionMapper = useContainer<SubscriptionMapper>(DependencyInjectId.SubscriptionMapper);
            const subscription = await subscriptionMapper.getCurrentSubscription();
            userStore.updateSubscription(subscription);
        }

        sendUserUUIDToExternalDirector(user);
        await Promise.resolve();
    };

    const sendUserUUIDToExternalDirector = (user: UserV2Model) => {
        const isCodepressoWebview = window.codepresso?.isCodepressoWebView ?? false;

        if(isCodepressoWebview) {
            window.codepresso!.sendToHost('sendUserInfo', {uuid: user.uuid, env: config.public.ENVIRONMENT})
        }
    }

    const getUserV2 = (): UserV2Model => {
        const userStore = useUserStore();
        return userStore.userV2;
    };

    const userLogOut = (): boolean => {
        const userStore = useUserStore();
        userStore.updateUserV2(new UserV2Model())
        userStore.updateSubscription(null)
        return userStore.isLogOut()
    }

    const userSignOut = async (): Promise<void> => {
        await userMapper.userSignOut();
    }

    const userSignIn = (email: string, password: string): Promise<string> => {
        return userMapper.userSignIn(email, password);
    };

    const userSignUp = (email: string, password: string, userType: string): Promise<boolean> => {
        return userMapper.userSignUp(email, password, userType);
    };

    const updateUser = (user: UserModel) => {
        const userStore = useUserStore();
        let newUser = new UserModel();
        newUser = user;
        userStore.updateUser(newUser);
    };

    const userAuthToken = (userId: number, token: string): Promise<string> => {
        return userMapper.userAuthToken(userId, token);
    }

    const userResendEmail = (email: string, emailType: ResendEmailType, assessmentId?: number): Promise<boolean> => {
        return userMapper.userResendEmail(email, emailType, assessmentId);
    }

    const userSendAuthEmail = (email: string): Promise<boolean> => {
        return userMapper.userSendAuthEmail(email);
    }

    const userPasswordChangeAuthEmail = (userId: number, token: string): Promise<boolean> => {
        return userMapper.userPasswordChangeAuthEmail(userId, token);
    }

    const userSendPasswordChangeAuthEmail = (email: string): Promise<boolean> => {
        return userMapper.userSendPasswordChangeAuthEmail(email);
    }

    const userPasswordChange = (userId: number, token: string, newPassword: string): Promise<UserPasswordChangeModel> => {
        return userMapper.userPasswordChange(userId, token, newPassword)
    }

    const userAccountSetting = (userAccountRequest: UserAccountRequest): Promise<boolean> => {
        return userMapper.userAccountSetting(userAccountRequest);
    }

    const updateUserTrackSkipFlag = (trackSkipFlag: { [key: string]: boolean }): Promise<boolean> => {
        return userMapper.updateUserTrackSkipFlag(trackSkipFlag);
    }

    const getCountryList = (): Promise<CountryModel[]> => {
        return userMapper.getCountryList(false);
    }

    const getCountryName = (): Promise<CountryModel[]> => {
        return userMapper.getCountryList(true);
    }

    const setUserCountryData = (userId: string, countryRegionId: string): Promise<boolean> => {
        return userMapper.setUserCountryData(userId, countryRegionId);
    }

    const setMarketingTerms = (allowMarketing: boolean): Promise<boolean> => {
        return userMapper.setMarketingTerms(allowMarketing);
    }

    const setUserLocale = (locale: string): Promise<boolean> => {
        return userMapper.setUserLocale(locale);
    }

    const changePassword = (newPassword: string, oldPassword?: string): Promise<boolean> => {
        return userMapper.changePassword(newPassword, oldPassword);
    }

    const deleteAccount = (): Promise<boolean> => {
        return userMapper.deleteAccount();
    }

    const getUserDashboardSummary = (): Promise<UserDashboardSummaryModel> => {
        return userMapper.getUserDashboardSummary();
    }

    const getBlockedEmailDomains = (): Promise<string[]> => {
        return userMapper.getBlockedEmailDomains();
    }

    return {
        setXCountry,
        setUser,
        getUserV2,
        userLogOut,
        userSignOut,
        updateUser,
        userSignIn,
        userSignUp,
        userAuthToken,
        userResendEmail,
        userSendAuthEmail,
        userPasswordChangeAuthEmail,
        userSendPasswordChangeAuthEmail,
        userPasswordChange,
        userAccountSetting,
        updateUserTrackSkipFlag,
        getCountryList,
        getCountryName,
        setUserCountryData,
        setUserLocale,
        changePassword,
        deleteAccount,
        setMarketingTerms,
        getUserDashboardSummary,
        getBlockedEmailDomains
    };
};
